import { getEvmClient } from '@1delta/providers'
import type { Address } from 'viem'
import { YieldFetcher } from '../../types'
import { annualizeRateDeltaPercent } from '../annualizeRate'

/**
 * Swell swBTC — intrinsic APR via share-rate delta.
 *
 * swBTC is a Yearn-V3-style ERC-4626 vault over WBTC (8-decimal share +
 * 8-decimal asset). Yield comes from Symbiotic + Swell-native rewards
 * that accrue to the vault's WBTC balance, reflected in the
 * `convertToAssets(1e8)` price. There is no public APR API; the only
 * source is the on-chain rate growth over a sampling window.
 *
 * Method (mirrors `iberaFetcher` / `stceloFetcher`):
 *   apr = (rateNow / rateThen - 1) * (yearSeconds / elapsedSeconds)
 *
 * Requires an archival Ethereum RPC for the historical read. Pruned
 * public nodes return zero for old state, which the sanity check
 * rejects — we cycle through the providers package's RPC list and
 * surface 0% if no archival node responds. That degrades gracefully:
 * swBTC's rate is currently 1:1 (no yield accrued yet), so any value
 * here only matters once Symbiotic rewards start landing.
 */

const CHAIN_ID = '1'
const SWBTC: Address = '0x8db2350d78abc13f5673a411d4700bcf87864dde'

/** Share unit — swBTC is 8-decimal (WBTC underlying). */
const ONE_E8 = 10n ** 8n

/** 7-day lookback. Symbiotic rewards accrue periodically; a multi-day
 *  window is needed for a stable figure. */
const WINDOW_SECONDS = 7 * 24 * 60 * 60
/** Ethereum block time ≈ 12s. We use this to estimate the historical
 *  block; the actual elapsed time is read back from block timestamps. */
const BLOCK_TIME_SECONDS = 12
const WINDOW_BLOCKS = BigInt(Math.floor(WINDOW_SECONDS / BLOCK_TIME_SECONDS))

const MAX_RPC_TRIES = 5

const CONVERT_TO_ASSETS_ABI = [
  {
    name: 'convertToAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256', name: 'shares' }],
    outputs: [{ type: 'uint256' }],
  },
] as const

const readRateAt = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: any,
  blockNumber?: bigint,
): Promise<bigint> =>
  client.readContract({
    address: SWBTC,
    abi: CONVERT_TO_ASSETS_ABI,
    functionName: 'convertToAssets',
    args: [ONE_E8],
    ...(blockNumber !== undefined ? { blockNumber } : {}),
  }) as Promise<bigint>

const computeApr = async (): Promise<number> => {
  let lastErr: unknown
  for (let rpcId = 0; rpcId < MAX_RPC_TRIES; rpcId++) {
    try {
      const client = getEvmClient(CHAIN_ID, rpcId)
      const head = await client.getBlockNumber()
      const pastBlock = head > WINDOW_BLOCKS ? head - WINDOW_BLOCKS : 0n

      const [rateNow, rateThen, headBlock, thenBlock] = await Promise.all([
        readRateAt(client),
        readRateAt(client, pastBlock),
        client.getBlock({ blockNumber: head }),
        client.getBlock({ blockNumber: pastBlock }),
      ])

      // Archive sanity: both rates must be real (≥ 1e8) and the rate
      // must be non-decreasing. Pruned nodes return 0 here.
      if (rateNow < ONE_E8 || rateThen < ONE_E8 || rateNow < rateThen) {
        throw new Error(
          `non-archival or invalid rates (now=${rateNow}, then=${rateThen})`,
        )
      }

      const elapsed = Number(headBlock.timestamp - thenBlock.timestamp)
      if (elapsed <= 0) throw new Error(`bad elapsed ${elapsed}`)

      const apr = annualizeRateDeltaPercent(rateNow, rateThen, elapsed)
      if (!Number.isFinite(apr) || apr < 0) throw new Error(`bad apr ${apr}`)
      return apr
    } catch (e) {
      lastErr = e
    }
  }
  throw new Error(
    `swBTC APR: no archival Ethereum RPC returned valid historical state (${String(lastErr)})`,
  )
}

export const swBtcFetcher: YieldFetcher = {
  label: 'SWBTC',
  fetch: async () => {
    // Canonical token-list assetGroup (see stcelo.ts) — drives the lending join.
    try {
      return { 'Swell Restaked BTC::SWBTC': await computeApr() }
    } catch {
      return { 'Swell Restaked BTC::SWBTC': 0 }
    }
  },
}
